import * as Sentry from '@sentry/react'
import type { ReactNode } from 'react'

interface ErrorFallbackProps {
  error: Error
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="mx-auto max-w-md px-6 py-10 text-center">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          Something went wrong
        </h1>
        <p className="mb-6 text-gray-500">
          An unexpected error occurred. Our team has been notified.
        </p>
        {import.meta.env.DEV && (
          <pre className="mb-6 max-h-40 overflow-auto rounded bg-gray-100 p-3 text-left text-xs text-gray-700">
            {error.message}
          </pre>
        )}
        <button
          onClick={resetError}
          className="text-blue-600 underline hover:text-blue-800"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

interface SentryErrorBoundaryProps {
  children: ReactNode
}

export function SentryErrorBoundary({ children }: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetError={resetError}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
